import { format } from "date-fns"
import { useEffect, useState } from "react"
import { Helmet } from "react-helmet"
import { useNavigate } from "react-router-dom"
import { toast, ToastContainer } from "react-toastify"
import logoHeader from "../../assets/logoHeader.png"
import Footer from "../../components/footer/Footer"
import { useHelp } from "../../components/help/HelpContext"
import { Tracking } from "../../interface/TrackingInterface"
import TrackingService, {
    TRACKING_ERROR_CODES,
    TrackingErrorResponse
} from "../../services/TrackinService"

const TOAST_ERROR_STYLE = {
    backgroundColor: "#f44336",
    color: "#fff",
    padding: "16px",
    borderRadius: "10px",
    border: "2px solid #D32F2F",
    fontSize: "16px"
}

const ERROR_MESSAGES = {
    EMPTY_FIELDS: "Por favor, preencha todos os campos",
    INVALID_CPF_CNPJ: "Por favor, preencha o campo de CPF/CNPJ corretamente",
    NOT_FOUND:
        "Não foi possível encontrar informações para os dados informados",
    GENERIC_ERROR: "Erro ao obter dados"
}

const ERROR_CODE_MESSAGES = {
    [TRACKING_ERROR_CODES.CONNECTION_REFUSED_EXECUTING_POST]:
        "Conexão recusada ao executar requisição POST",
    [TRACKING_ERROR_CODES.CONNECTION_REFUSED]: "Conexão recusada pelo servidor",
    [TRACKING_ERROR_CODES.NETWORK_ERROR]: "Erro de conexão de rede",
    [TRACKING_ERROR_CODES.TIMEOUT_ERROR]: "Tempo limite de conexão excedido",
    [TRACKING_ERROR_CODES.API_ERROR]: "Erro na API de rastreamento",
    [TRACKING_ERROR_CODES.INVALID_RESPONSE]: "Resposta inválida do servidor",
    [TRACKING_ERROR_CODES.NO_INVOICE_FOUND]: "Nenhuma nota fiscal encontrada",
    [TRACKING_ERROR_CODES.NOT_FOUND]: "Dados não encontrados",
    [TRACKING_ERROR_CODES.INVALID_DATA]: "Dados inválidos",
    [TRACKING_ERROR_CODES.UNKNOWN_ERROR]: "Erro desconhecido"
}

const VALIDATION_RULES = {
    MIN_CPF_CNPJ_LENGTH: 11,
    MAX_CPF_CNPJ_LENGTH: 14
}

// Componente do Botão Flutuante de Ajuda
const FloatingHelpButton = ({
    isVisible,
    onClick
}: {
    isVisible: boolean
    onClick: () => void
}) => {
    if (!isVisible) return null

    return (
        <div className="fixed bottom-6 right-6 z-40 animate-slideIn">
            <button
                onClick={onClick}
                className="w-16 h-16 bg-gradient-to-r from-secondary-1 to-secondary-2 rounded-full shadow-2xl hover:shadow-3xl transform hover:scale-110 transition-all duration-300 animate-bounce-pulse group"
                aria-label="Precisa de ajuda?"
            >
                <div className="flex items-center justify-center w-full h-full">
                    <svg
                        className="w-8 h-8 text-white group-hover:rotate-12 transition-transform duration-300"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                    </svg>
                </div>
                {/* Indicador de notificação */}
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full animate-ping"></div>

                {/* Tooltip */}
                <div className="absolute bottom-full right-0 mb-2 px-3 py-1 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">
                    Precisa de ajuda?
                    <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                </div>
            </button>
        </div>
    )
}

// Componente do Popup de Ajuda
const HelpPopup = ({
    isOpen,
    onClose,
    onWhatsApp
}: {
    isOpen: boolean
    onClose: () => void
    onWhatsApp: () => void
}) => {
    if (!isOpen) return null

    const handleWhatsApp = () => {
        onWhatsApp()
        onClose()
    }

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-4 animate-fadeIn"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl transform transition-all duration-300 animate-scaleIn relative"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Botão de fechar */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                    aria-label="Fechar"
                >
                    <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                        />
                    </svg>
                </button>

                <div className="text-center">
                    {/* Ícone mais amigável */}
                    <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-gradient-to-r from-secondary-1/20 to-secondary-2/20 mb-6">
                        <svg
                            className="h-8 w-8 text-secondary-1"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                        </svg>
                    </div>

                    {/* Título mais amigável */}
                    <h3 className="text-xl font-semibold text-gray-800 mb-3">
                        Está com dificuldades?
                    </h3>

                    {/* Mensagem mais empática */}
                    <p className="text-gray-600 mb-8 leading-relaxed">
                        Não se preocupe! Às vezes os dados podem demorar um
                        pouco para aparecer no sistema. Nossa equipe está aqui
                        para te ajudar a encontrar sua encomenda.
                    </p>

                    {/* Botões mais atrativos */}
                    <div className="space-y-3">
                        <button
                            onClick={handleWhatsApp}
                            className="w-full px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-secondary-1 to-secondary-2 rounded-xl hover:from-secondary-2 hover:to-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary-1 transform hover:scale-105 transition-all duration-200 shadow-lg"
                        >
                            <div className="flex items-center justify-center space-x-2">
                                <svg
                                    className="w-5 h-5"
                                    fill="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
                                </svg>
                                <span>Falar com nossa equipe</span>
                            </div>
                        </button>

                        <button
                            onClick={onClose}
                            className="w-full px-6 py-3 text-sm font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-xl hover:bg-gray-100 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all duration-200"
                        >
                            Tentar novamente
                        </button>
                    </div>

                    {/* Dica adicional */}
                    <p className="text-xs text-gray-400 mt-4">
                        💡 Dica: Verifique se o CPF/CNPJ e número da nota fiscal
                        estão corretos
                    </p>
                </div>
            </div>
        </div>
    )
}

export default function TrackingScreen() {
    const [cpfOrCNpj, setCpfOrCnpj] = useState("")
    const [invoiceNumber, setInvoiceNumber] = useState("")
    const [showDetails, setShowDetails] = useState(false)
    const [isHidden, setIsHidden] = useState(true)
    const [trackingInformation, setTrackingInformation] = useState<Tracking>()
    const [isLoading, setIsLoading] = useState(false)
    const [consecutiveErrors, setConsecutiveErrors] = useState(0)
    const [showHelpButton] = useState(false)
    const [showHelpPopup, setShowHelpPopup] = useState(false)

    const navigate = useNavigate()
    const { setShowHelpButton, showHelpAfterErrors } = useHelp()

    const showErrorToast = (message: string, errorCode?: string) => {
        const fullMessage = errorCode
            ? `${message} [Código: ${errorCode}]`
            : message
        toast.error(fullMessage, { style: TOAST_ERROR_STYLE })

        setConsecutiveErrors((prev) => {
            const newCount = prev + 1
            // Se chegou a 3 erros consecutivos, mostra o botão flutuante
            if (newCount >= 3) {
                setShowHelpButton(true)
            }
            return newCount
        })
    }

    const handleWhatsAppRedirect = () => {
        // Número do WhatsApp da LogPlace (substitua pelo número correto)
        const whatsappNumber = "5511999999999" // TODO: Substituir pelo número real do WhatsApp da LogPlace
        const message = encodeURIComponent(
            "Olá! 👋\n\n" +
                "Estou no site da LogPlace tentando rastrear uma encomenda, mas não estou conseguindo encontrar as informações.\n\n" +
                "Vocês poderiam me ajudar?\n\n" +
                "Obrigado! 😊"
        )
        const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${message}`
        window.open(whatsappUrl, "_blank")
    }

    const handleShowHelpPopup = () => {
        setShowHelpPopup(true)
    }

    const handleCloseHelpPopup = () => {
        setShowHelpPopup(false)
        // Mantém o botão flutuante visível até que o usuário tenha sucesso
        // setShowHelpButton(false) // Comentado para manter o botão visível
        // setConsecutiveErrors(0) // Comentado para manter o contador
    }

    const resetErrorCount = () => {
        setShowHelpButton(false)
        setConsecutiveErrors(0)
    }

    const formatDate = (
        dateString: string | null,
        includeTime = false
    ): string => {
        if (!dateString) return "N/A"

        try {
            const date = dateString.includes("T")
                ? new Date(dateString)
                : new Date(dateString.replace(" ", "T"))

            return format(
                date,
                includeTime ? "dd/MM/yyyy HH:mm:ss" : "dd/MM/yyyy"
            )
        } catch {
            return "N/A"
        }
    }

    const formatOccurrenceDescription = (
        description: string
    ): { main: string; details: string } => {
        if (!description.includes("-")) {
            return { main: description, details: "" }
        }

        const parts = description.split("-")
        const main = parts[0]
        const details = parts.slice(1).join(" ").trim()

        return { main, details }
    }

    const validateInputs = (): boolean => {
        if (!cpfOrCNpj || !invoiceNumber) {
            showErrorToast(ERROR_MESSAGES.EMPTY_FIELDS)
            return false
        }

        if (
            cpfOrCNpj.length < VALIDATION_RULES.MIN_CPF_CNPJ_LENGTH ||
            cpfOrCNpj.length > VALIDATION_RULES.MAX_CPF_CNPJ_LENGTH
        ) {
            showErrorToast(ERROR_MESSAGES.INVALID_CPF_CNPJ)
            return false
        }

        return true
    }

    const fetchTrackingData = async () => {
        setIsLoading(true)

        try {
            const response = await TrackingService.getInformation(
                cpfOrCNpj,
                invoiceNumber
            )
            const parsedData = JSON.parse(response.data.json)
            setTrackingInformation(parsedData)
            setShowDetails(true)
            // Reseta o contador de erros quando a consulta for bem-sucedida
            resetErrorCount()
        } catch (error: any) {
            if (error.code && error.message) {
                const customError = error as TrackingErrorResponse
                const userMessage =
                    ERROR_CODE_MESSAGES[customError.code] ||
                    ERROR_MESSAGES.GENERIC_ERROR
                showErrorToast(userMessage, customError.code)
            } else {
                showErrorToast(
                    ERROR_MESSAGES.NOT_FOUND,
                    TRACKING_ERROR_CODES.UNKNOWN_ERROR
                )
            }
        } finally {
            setIsLoading(false)
        }
    }

    const handleClick = (value: string) => {
        navigate("/" + value)
    }

    const handleNewSearch = () => {
        setShowDetails(false)
        setIsHidden(true)
        setCpfOrCnpj("")
        setInvoiceNumber("")
        setTrackingInformation(undefined)
    }

    const handleConsult = async () => {
        if (!validateInputs()) return
        await fetchTrackingData()
    }

    useEffect(() => {
        document.title = "Logplace - Rastreamento"
    }, [])

    // Efeito para reagir às mudanças no contador de erros
    useEffect(() => {
        if (consecutiveErrors >= 1) {
            showHelpAfterErrors(consecutiveErrors)
        }
    }, [consecutiveErrors, showHelpAfterErrors])

    useEffect(() => {
        if (showDetails) {
            setIsHidden(false)
        } else {
            const timer = setTimeout(() => setIsHidden(true), 10000)
            return () => clearTimeout(timer)
        }
    }, [showDetails])

    const getContainerClasses = () => {
        const baseClasses =
            "px-4 xl:px-44 h-[40rem] lg:h-[45rem] flex flex-row justify-center xl:justify-center w-full bg-gradient-to-b from-white from-1% via-secondary-1 via-30% via-secondary-2 via-30% to-primary to-80% z-0"
        return showDetails
            ? `${baseClasses} justify-center items-center`
            : baseClasses
    }

    const getSearchFormClasses = () => {
        const baseClasses =
            "h-[45rem] xl:w-2/3 justify-center items-center flex transition-opacity duration-500"
        const visibilityClasses = showDetails
            ? "opacity-0 pointer-events-none"
            : "opacity-100"
        const hiddenClasses = isHidden && !showDetails ? "m-0" : "hidden"

        return `${baseClasses} ${visibilityClasses} ${hiddenClasses}`
    }

    const getDetailsClasses = () => {
        const baseClasses =
            "w-full h-[80rem] lg:h-[42rem] rounded-lg bg-white transition-opacity duration-1000 p-4 space-y-2"
        const visibilityClasses = showDetails
            ? "opacity-100"
            : "opacity-0 pointer-events-none hidden"
        const hiddenClasses = isHidden && showDetails ? "hidden" : ""

        return `${baseClasses} ${visibilityClasses} ${hiddenClasses}`
    }

    const NotaFiscalInfo = () => {
        if (!trackingInformation) return null

        const { notafiscal, ocorrencias } = trackingInformation

        const ocorrenciaKronos04 = ocorrencias?.find(
            (ocorrencia) => ocorrencia.codigoKronos === "04"
        )

        return (
            <div className="flex flex-col p-4 space-y-2 rounded-lg h-2/6 xl:space-y-0 xl:h-1/4 bg-slate-200 justify-evenly lg:flex-row xl:space-x-4">
                <div className="lg:w-2/4">
                    <p>
                        Número da Nota: {notafiscal.numero} - Série:{" "}
                        {notafiscal.serie}
                    </p>
                    <div className="w-full p-2 bg-white rounded-lg">
                        <p>Destinatário: {notafiscal.destinatario}</p>
                        <p>CPF/CNPJ: {notafiscal.cpfCnpjDestinatario}</p>
                    </div>
                </div>
                <div className="lg:w-1/4">
                    <p>Fornecedor: {notafiscal.cnpjFornecedor}</p>
                    <p>Emissão: {formatDate(notafiscal.emissao)}</p>
                    <p>Data Limite: {formatDate(notafiscal.datalimite)}</p>
                    {ocorrenciaKronos04 && (
                        <p>
                            Data Programada:{" "}
                            {formatDate(ocorrenciaKronos04.dataProgramada)}
                        </p>
                    )}
                </div>
                <div className="lg:w-1/4">
                    <button
                        onClick={handleNewSearch}
                        className="w-full h-20 font-bold text-white rounded-lg bg-secondary-1"
                    >
                        Nova Busca
                    </button>
                </div>
            </div>
        )
    }

    const OccurrencesTable = () => {
        if (!trackingInformation?.ocorrencias) return null

        return (
            <div className="p-4 overflow-y-auto rounded-lg h-4/6 xl:h-3/4 bg-slate-200">
                <table className="w-full">
                    <thead>
                        <tr className="flex flex-row flex-1">
                            <th className="text-center flex-3">Descrição</th>
                            <th className="flex-1 text-center">Status</th>
                            <th className="flex-1 text-center">Data</th>
                        </tr>
                    </thead>
                    <tbody>
                        {trackingInformation.ocorrencias.map((ocorrencia) => {
                            const { main, details } =
                                formatOccurrenceDescription(
                                    ocorrencia.descricaoKronos
                                )

                            return (
                                <tr
                                    className="flex flex-1 border-b-2 border-gray-600"
                                    key={`${ocorrencia.numOcorrencia}-${Math.random()}`}
                                >
                                    <td className="p-2 text-sm flex-3">
                                        {main}
                                        {details && (
                                            <>
                                                <br />
                                                {details}
                                            </>
                                        )}
                                    </td>
                                    <td className="flex-1 p-2 font-semibold text-center">
                                        {ocorrencia.status}
                                    </td>
                                    <td className="flex-1 text-sm text-center">
                                        {formatDate(ocorrencia.momento, true)}
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>
        )
    }

    return (
        <>
            <Helmet>
                <title>LogPlace - Rastreamento</title>
                <meta
                    name="description"
                    content="Oferecemos soluções logísticas sob medida para sua operação. Fulfillment, B2B, B2C com inteligência e tecnologia."
                />
                <meta
                    name="keywords"
                    content="logística, fulfillment, B2C, B2B, transporte, soluções logísticas, entregas, e-commerce"
                />
                <meta name="author" content="LogPlace" />
                <meta
                    name="viewport"
                    content="width=device-width, initial-scale=1.0"
                />
                <meta property="og:title" content="Rastreamento" />
                <meta
                    property="og:description"
                    content="Somos líderes em soluções logísticas para o pequeno, médio e-commerce e grandes operações de varejo."
                />
                <meta property="og:url" content="https://logplace.com.br" />
                <meta property="og:type" content="website" />
            </Helmet>

            <div className="relative flex flex-col flex-1 w-full h-screen overflow-y-auto bg-white">
                <ToastContainer
                    position="top-right"
                    autoClose={1500}
                    hideProgressBar={false}
                    newestOnTop={false}
                    closeOnClick
                    rtl={false}
                    icon={false}
                    limit={1}
                    pauseOnFocusLoss
                    draggable
                    pauseOnHover
                />

                {/* Header */}
                <div className="flex flex-col items-center justify-center w-full py-6 space-y-4">
                    {/* Logo */}
                    <button
                        onClick={() => handleClick("home")}
                        className="flex items-center justify-center h-24 bg-white rounded-lg w-72 shadow-sm"
                    >
                        <img
                            className="w-[100%] h-[90%]"
                            src={logoHeader}
                            alt="LogPlace Logo"
                        />
                    </button>

                    {/* Botão B2B integrado ao layout */}
                    <div className="flex items-center justify-center">
                        <button
                            onClick={() => navigate("/login")}
                            className="group relative px-8 py-3 bg-gradient-to-r from-secondary-1 to-secondary-2 hover:from-secondary-2 hover:to-primary text-white font-bold rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 ease-in-out"
                        >
                            <div className="flex items-center space-x-3">
                                <svg
                                    className="w-5 h-5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                    />
                                </svg>
                                <span className="text-base font-semibold tracking-wide">
                                    RASTREAMENTO EM MASSA
                                </span>
                                <div className="bg-yellow-400 text-yellow-900 text-xs font-bold px-2 py-1 rounded-full">
                                    NOVO
                                </div>
                            </div>
                            <div className="absolute inset-0 rounded-full bg-white opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
                        </button>
                    </div>
                </div>

                {/* Main Content */}
                <div className={getContainerClasses()}>
                    {/* Welcome Text */}
                    <div
                        className={`hidden h-[50rem] w-1/2 justify-center items-center ${
                            showDetails
                                ? "hidden opacity-0 w-0"
                                : "opacity-100 xl:flex"
                        }`}
                    >
                        <p className="text-4xl font-bold text-white">
                            Olá! <br />
                            Veja aqui o status do
                            <br /> envio das suas
                            <br /> encomendas
                        </p>
                    </div>

                    {/* Formulário de Consulta - Campos para CPF/CNPJ e Nota Fiscal */}
                    <div className={getSearchFormClasses()}>
                        <div className="flex flex-col w-full p-8 bg-white rounded-lg shadow-lg md:w-4/5 h-2/3">
                            <div className="flex items-center justify-center text-justify flex-2 xl:px-24 2xl:px-32">
                                <p className="mt-2 text-lg font-bold text-center text-black">
                                    Digite as informações necessárias para ver o
                                    acompanhamento do seu envio
                                </p>
                            </div>

                            <div className="flex flex-col items-center justify-center space-y-4 flex-2">
                                <input
                                    type="number"
                                    maxLength={14}
                                    value={cpfOrCNpj}
                                    onChange={(e) =>
                                        setCpfOrCnpj(e.target.value)
                                    }
                                    placeholder="Informe o seu CPF/CNPJ:"
                                    className="w-full h-20 p-4 text-black border border-black rounded-lg"
                                    disabled={isLoading}
                                />
                                <input
                                    type="number"
                                    maxLength={12}
                                    value={invoiceNumber}
                                    onChange={(e) =>
                                        setInvoiceNumber(e.target.value)
                                    }
                                    placeholder="Informe o número da nota fiscal:"
                                    className="w-full h-20 p-4 text-black border border-black rounded-lg"
                                    disabled={isLoading}
                                />
                            </div>

                            <div className="flex items-center justify-center flex-1 mt-4 space-x-2">
                                <button
                                    onClick={handleConsult}
                                    disabled={isLoading}
                                    className="w-full h-20 font-bold text-white rounded-lg bg-secondary-1 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isLoading ? "Consultando..." : "Consultar"}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Results */}
                    <div className={getDetailsClasses()}>
                        {trackingInformation && (
                            <>
                                <NotaFiscalInfo />
                                <OccurrencesTable />
                            </>
                        )}
                    </div>
                </div>

                <Footer />

                <FloatingHelpButton
                    isVisible={showHelpButton}
                    onClick={handleShowHelpPopup}
                />

                <HelpPopup
                    isOpen={showHelpPopup}
                    onClose={handleCloseHelpPopup}
                    onWhatsApp={handleWhatsAppRedirect}
                />
            </div>
        </>
    )
}
